import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Grid, Box, Typography, Tabs, Tab, Divider, Chip } from '@mui/material'
import { Edit, Print, QrCode2, Delete, Add, Download } from '@mui/icons-material'
import { PageHeader, Button, Card, StatusChip, LoadingSpinner, Modal } from '@components/common'
import { AssetForm } from '@components/assets'
import api from '@services/api'

const AssetDetail = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [asset, setAsset] = useState(null)
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState(0)
  const [editFormOpen, setEditFormOpen] = useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [qrModalOpen, setQrModalOpen] = useState(false)
  const [generatingQR, setGeneratingQR] = useState(false)
  const printRef = useRef()

  const fetchAsset = async () => {
    try {
      const response = await api.get(`/assets/assets/${id}/`)
      setAsset(response.data)
    } catch (error) {
      console.error('Error fetching asset:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAsset()
  }, [id])

  const handleDelete = async () => {
    setDeleting(true)
    try {
      await api.delete(`/assets/assets/${id}/`)
      navigate('/assets')
    } catch (error) {
      console.error('Error deleting asset:', error)
    } finally {
      setDeleting(false)
    }
  }

  const handleEditSuccess = () => {
    fetchAsset()
  }

  const handlePrint = () => {
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${asset.name} - Asset Details</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { color: #1976d2; margin-bottom: 5px; }
          .subtitle { color: #666; margin-bottom: 20px; }
          .section { margin-bottom: 20px; }
          .section-title { font-weight: bold; color: #333; margin-bottom: 10px; border-bottom: 1px solid #ddd; padding-bottom: 5px; }
          .row { display: flex; margin-bottom: 8px; }
          .label { width: 150px; color: #666; }
          .value { flex: 1; font-weight: 500; }
          .qr-section { text-align: center; margin-top: 20px; }
          .qr-section img { width: 150px; height: 150px; }
          @media print { body { print-color-adjust: exact; -webkit-print-color-adjust: exact; } }
        </style>
      </head>
      <body>
        <h1>${asset.name}</h1>
        <p class="subtitle">Asset Tag: ${asset.asset_tag || 'N/A'}</p>

        <div class="section">
          <div class="section-title">General Information</div>
          <div class="row"><span class="label">Status:</span><span class="value">${asset.status || '-'}</span></div>
          <div class="row"><span class="label">Criticality:</span><span class="value">${asset.criticality || '-'}</span></div>
          <div class="row"><span class="label">Category:</span><span class="value">${asset.category?.name || '-'}</span></div>
          <div class="row"><span class="label">Manufacturer:</span><span class="value">${asset.manufacturer?.name || '-'}</span></div>
          <div class="row"><span class="label">Model Number:</span><span class="value">${asset.model_number || '-'}</span></div>
          <div class="row"><span class="label">Serial Number:</span><span class="value">${asset.serial_number || '-'}</span></div>
        </div>

        <div class="section">
          <div class="section-title">Location</div>
          <div class="row"><span class="label">Location:</span><span class="value">${asset.location?.name || '-'}${asset.room_number ? ` - Room ${asset.room_number}` : ''}</span></div>
          <div class="row"><span class="label">Department:</span><span class="value">${asset.department_name || '-'}</span></div>
        </div>

        <div class="section">
          <div class="section-title">Financial Information</div>
          <div class="row"><span class="label">Purchase Date:</span><span class="value">${asset.purchase_date || '-'}</span></div>
          <div class="row"><span class="label">Purchase Cost:</span><span class="value">${asset.purchase_cost ? `UGX ${Number(asset.purchase_cost).toLocaleString()}` : '-'}</span></div>
          <div class="row"><span class="label">Warranty Expiry:</span><span class="value">${asset.warranty_expiry || '-'}</span></div>
          <div class="row"><span class="label">Expected Life:</span><span class="value">${asset.expected_life_years ? `${asset.expected_life_years} years` : '-'}</span></div>
        </div>

        ${asset.description ? `<div class="section"><div class="section-title">Description</div><p>${asset.description}</p></div>` : ''}
        ${asset.notes ? `<div class="section"><div class="section-title">Notes</div><p>${asset.notes}</p></div>` : ''}

        ${asset.qr_code ? `<div class="qr-section"><img src="${asset.qr_code}" alt="QR Code" /><p>Scan for asset details</p></div>` : ''}

        <p style="margin-top: 30px; font-size: 12px; color: #999;">Printed on ${new Date().toLocaleDateString()} from UCI CMMS</p>
      </body>
      </html>
    `
    const printWindow = window.open('', '_blank')
    printWindow.document.write(printContent)
    printWindow.document.close()
    printWindow.onload = () => {
      printWindow.print()
    }
  }

  const handleDownloadQR = () => {
    if (asset.qr_code) {
      const link = document.createElement('a')
      link.href = asset.qr_code
      link.download = `${asset.asset_tag || asset.name}_qr_code.png`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  }

  const handleGenerateQR = async () => {
    setGeneratingQR(true)
    try {
      const response = await api.post(`/assets/assets/${id}/generate_qr_code/`)
      setAsset(response.data)
    } catch (error) {
      console.error('Error generating QR code:', error)
    } finally {
      setGeneratingQR(false)
    }
  }

  if (loading) {
    return <LoadingSpinner message="Loading asset details..." />
  }

  if (!asset) {
    return (
      <Box textAlign="center" py={4}>
        <Typography>Asset not found</Typography>
        <Button onClick={() => navigate('/assets')} sx={{ mt: 2 }}>
          Back to Assets
        </Button>
      </Box>
    )
  }

  return (
    <Box>
      <PageHeader
        title={asset.name}
        subtitle={`Asset Tag: ${asset.asset_tag || 'N/A'}`}
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Assets', path: '/assets' },
          { label: asset.name },
        ]}
        actions={
          <>
            <Button
              variant="outlined"
              startIcon={<QrCode2 />}
              onClick={() => setQrModalOpen(true)}
            >
              QR Code
            </Button>
            <Button
              variant="outlined"
              startIcon={<Print />}
              onClick={handlePrint}
            >
              Print
            </Button>
            <Button
              startIcon={<Edit />}
              onClick={() => setEditFormOpen(true)}
            >
              Edit
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<Delete />}
              onClick={() => setDeleteModalOpen(true)}
            >
              Delete
            </Button>
          </>
        }
      />

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card>
            <Box mb={2}>
              <Tabs
                value={activeTab}
                onChange={(_, newValue) => setActiveTab(newValue)}
              >
                <Tab label="Details" />
                <Tab label="Work Orders" />
                <Tab label="PM Schedule" />
                <Tab label="Documents" />
              </Tabs>
            </Box>
            <Divider sx={{ mb: 3 }} />

            {activeTab === 0 && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">
                    Status
                  </Typography>
                  <Box mt={0.5}>
                    <StatusChip status={asset.status} />
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">
                    Criticality
                  </Typography>
                  <Box mt={0.5}>
                    <StatusChip status={asset.criticality} type="criticality" />
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">
                    Category
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {asset.category?.name || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">
                    Manufacturer
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {asset.manufacturer?.name || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">
                    Model Number
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {asset.model_number || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">
                    Serial Number
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {asset.serial_number || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">
                    Location
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {asset.location?.name || '-'}
                    {asset.room_number && ` - Room ${asset.room_number}`}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">
                    Department
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {asset.department_name || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">
                    Purchase Date
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {asset.purchase_date || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">
                    Purchase Cost
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {asset.purchase_cost ? `UGX ${Number(asset.purchase_cost).toLocaleString()}` : '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">
                    Warranty Expiry
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {asset.warranty_expiry || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">
                    Expected Life
                  </Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {asset.expected_life_years ? `${asset.expected_life_years} years` : '-'}
                  </Typography>
                </Grid>
                {asset.description && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">
                      Description
                    </Typography>
                    <Typography variant="body2">
                      {asset.description}
                    </Typography>
                  </Grid>
                )}
                {asset.notes && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">
                      Notes
                    </Typography>
                    <Typography variant="body2">
                      {asset.notes}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            )}

            {activeTab === 1 && (
              <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="subtitle2">Work Orders</Typography>
                  <Button size="small" startIcon={<Add />}>
                    Create Work Order
                  </Button>
                </Box>
                <Typography color="text.secondary">
                  No work orders found for this asset
                </Typography>
              </Box>
            )}

            {activeTab === 2 && (
              <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="subtitle2">PM Schedule</Typography>
                  <Button size="small" startIcon={<Add />}>
                    Add PM Schedule
                  </Button>
                </Box>
                <Typography color="text.secondary">
                  No preventive maintenance scheduled for this asset
                </Typography>
              </Box>
            )}

            {activeTab === 3 && (
              <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="subtitle2">Documents</Typography>
                  <Button size="small" startIcon={<Add />}>
                    Upload Document
                  </Button>
                </Box>
                <Typography color="text.secondary">
                  No documents uploaded for this asset
                </Typography>
              </Box>
            )}
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          {/* Asset Images */}
          {(asset.image || asset.image_2 || asset.image_3 || asset.qr_code) && (
            <Card title="Images" sx={{ mb: 3 }}>
              <Box display="flex" flexDirection="column" gap={2}>
                {asset.image && (
                  <Box
                    component="img"
                    src={asset.image}
                    alt={asset.name}
                    sx={{
                      width: '100%',
                      maxHeight: 200,
                      objectFit: 'contain',
                      borderRadius: 1,
                      bgcolor: 'grey.100',
                    }}
                  />
                )}
                <Box display="flex" gap={1} flexWrap="wrap">
                  {asset.image_2 && (
                    <Box
                      component="img"
                      src={asset.image_2}
                      alt={`${asset.name} - 2`}
                      sx={{
                        width: 80,
                        height: 60,
                        objectFit: 'cover',
                        borderRadius: 1,
                        cursor: 'pointer',
                        border: '1px solid',
                        borderColor: 'divider',
                      }}
                    />
                  )}
                  {asset.image_3 && (
                    <Box
                      component="img"
                      src={asset.image_3}
                      alt={`${asset.name} - 3`}
                      sx={{
                        width: 80,
                        height: 60,
                        objectFit: 'cover',
                        borderRadius: 1,
                        cursor: 'pointer',
                        border: '1px solid',
                        borderColor: 'divider',
                      }}
                    />
                  )}
                  {asset.qr_code && (
                    <Box
                      component="img"
                      src={asset.qr_code}
                      alt="QR Code"
                      sx={{
                        width: 80,
                        height: 80,
                        objectFit: 'contain',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                        bgcolor: 'white',
                      }}
                    />
                  )}
                </Box>
              </Box>
            </Card>
          )}

          <Card title="Quick Info">
            <Box display="flex" flexDirection="column" gap={2}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Asset Tag
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {asset.asset_tag || 'Not assigned'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Created By
                </Typography>
                <Typography variant="body2">
                  {asset.created_by_name || '-'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Created At
                </Typography>
                <Typography variant="body2">
                  {new Date(asset.created_at).toLocaleDateString()}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Last Updated
                </Typography>
                <Typography variant="body2">
                  {new Date(asset.updated_at).toLocaleDateString()}
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>
      </Grid>

      <AssetForm
        open={editFormOpen}
        onClose={() => setEditFormOpen(false)}
        asset={asset}
        onSuccess={handleEditSuccess}
      />

      <Modal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Delete Asset"
        maxWidth="xs"
        actions={
          <>
            <Button variant="outlined" onClick={() => setDeleteModalOpen(false)} disabled={deleting}>
              Cancel
            </Button>
            <Button color="error" onClick={handleDelete} loading={deleting}>
              Delete
            </Button>
          </>
        }
      >
        <Typography>
          Are you sure you want to delete "{asset.name}"? This action cannot be undone.
        </Typography>
      </Modal>

      <Modal
        open={qrModalOpen}
        onClose={() => setQrModalOpen(false)}
        title="Asset QR Code"
        maxWidth="xs"
        actions={
          <>
            <Button variant="outlined" onClick={() => setQrModalOpen(false)}>
              Close
            </Button>
            {asset.qr_code ? (
              <Button startIcon={<Download />} onClick={handleDownloadQR}>
                Download
              </Button>
            ) : (
              <Button onClick={handleGenerateQR} loading={generatingQR}>
                Generate QR Code
              </Button>
            )}
          </>
        }
      >
        <Box display="flex" flexDirection="column" alignItems="center" py={2}>
          {asset.qr_code ? (
            <>
              <Box
                component="img"
                src={asset.qr_code}
                alt="Asset QR Code"
                sx={{
                  width: 200,
                  height: 200,
                  objectFit: 'contain',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  bgcolor: 'white',
                  p: 1,
                }}
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                {asset.asset_tag || asset.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Scan to view asset details
              </Typography>
            </>
          ) : (
            <Box textAlign="center">
              <QrCode2 sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
              <Typography color="text.secondary">
                No QR code has been generated for this asset yet.
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Click "Generate QR Code" to create one.
              </Typography>
            </Box>
          )}
        </Box>
      </Modal>
    </Box>
  )
}

export default AssetDetail
